import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import {
  ArrowLeft, ClipboardCheck, FileCheck, Search as SearchIcon, AlertTriangle,
  CheckCircle, XCircle, Clock, ChevronRight, Eye, Layers, BarChart3, Shield,
  Plus, Trash2, Pencil, Send, ThumbsUp, ThumbsDown
} from 'lucide-react';
import { toast } from 'sonner';

const API = BACKEND_URL;

const statusBadge = (status) => {
  const map = {
    pending: 'bg-amber-100 text-amber-800',
    active: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-blue-100 text-blue-800',
    passed: 'bg-green-100 text-green-800',
    completed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    rejected: 'bg-red-100 text-red-800',
    approved: 'bg-green-100 text-green-800',
    draft: 'bg-gray-100 text-gray-700',
    pending_approval: 'bg-amber-100 text-amber-800',
  };
  return map[status] || 'bg-gray-100 text-gray-700';
};

const QAOverview = ({ user }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('qa-checklists');
  const [projects, setProjects] = useState([]);
  // Unified create-dialog state. `kind` ∈ 'digital'|'qa'|'inspection'|'change'.
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createKind, setCreateKind] = useState('digital');
  const [newCl, setNewCl] = useState({ project_id: '', title: '', description: '', assigned_to: '' });

  // Unified edit-dialog state. `kind` ∈ 'qa'|'inspection'|'change'.
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editKind, setEditKind] = useState('qa');
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const isGuest = user?.role === 'guest';
  const canApprove = user?.role === 'administrator' || user?.role === 'project_manager';
  // canManage = create / edit. Open to any non-guest. Backend enforces project scoping
  // (investors → projects they fund, social actors → projects they own).
  const canManage = !isGuest;
  // Delete remains restricted to Admin / PM
  const canDelete = canApprove;

  useEffect(() => { fetchData(); fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/api/projects`, { headers });
      setProjects(res.data || []);
    } catch { /* non-blocking */ }
  };

  const openCreate = (kind) => {
    setCreateKind(kind);
    setNewCl({ project_id: '', title: '', description: '', assigned_to: '' });
    setShowCreateDialog(true);
  };

  const createKindMeta = {
    digital:    { label: 'Digital Checklist',  endpoint: 'digital-checklists', titleKey: 'New Digital Checklist',  body: (n) => ({ title: n.title, description: n.description, items: [] }) },
    qa:         { label: 'QA Checklist',       endpoint: 'qa-checklists',      titleKey: 'New QA Checklist',       body: (n) => ({ title: n.title, description: n.description, checklist_items: [], assigned_to: n.assigned_to || '' }) },
    inspection: { label: 'Inspection',         endpoint: 'qa-inspections',     titleKey: 'New Inspection',         body: (n) => ({ title: n.title, description: n.description, status: 'pending' }) },
    change:     { label: 'Change Request',     endpoint: 'changes',            titleKey: 'New Change Request',     body: (n) => ({ title: n.title, description: n.description, impact_assessment: {} }) },
  };

  const handleCreate = async () => {
    if (!newCl.project_id || !newCl.title) {
      toast.error('Project and title are required');
      return;
    }
    const meta = createKindMeta[createKind];
    try {
      await axios.post(
        `${API}/api/projects/${newCl.project_id}/${meta.endpoint}`,
        meta.body(newCl),
        { headers },
      );
      toast.success(`${meta.label} created`);
      setShowCreateDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to create ${meta.label}`);
    }
  };

  const handleDelete = async (kind, projectId, id) => {
    const meta = createKindMeta[kind];
    if (!window.confirm(`Delete this ${meta.label}? This cannot be undone.`)) return;
    try {
      await axios.delete(
        `${API}/api/projects/${projectId}/${meta.endpoint}/${id}`,
        { headers },
      );
      toast.success(`${meta.label} deleted`);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to delete ${meta.label}`);
    }
  };

  // ── Edit dialog config ──
  const editKindMeta = {
    qa: {
      label: 'QA Checklist',
      endpoint: 'qa-checklists',
      method: 'put',
      titleField: 'title',
      fields: [
        { key: 'title', label: 'Title', type: 'text' },
        { key: 'description', label: 'Description', type: 'text' },
        { key: 'assigned_to', label: 'Assigned to (username)', type: 'text' },
        { key: 'overall_status', label: 'Overall status', type: 'select', options: ['pending', 'in_progress', 'passed', 'failed'] },
      ],
    },
    inspection: {
      label: 'Inspection',
      endpoint: 'qa-inspections',
      method: 'patch',
      titleField: 'item_name',
      fields: [
        { key: 'item_name', label: 'Item name', type: 'text' },
        { key: 'item_type', label: 'Item type', type: 'text' },
        { key: 'notes', label: 'Notes', type: 'textarea' },
        { key: 'score', label: 'Score (0–100)', type: 'number' },
        { key: 'status', label: 'Status', type: 'select', options: ['pending', 'passed', 'failed'] },
      ],
    },
    change: {
      label: 'Change Request',
      endpoint: 'changes',
      method: 'patch',
      titleField: 'title',
      fields: [
        { key: 'title', label: 'Title', type: 'text' },
        { key: 'description', label: 'Description', type: 'textarea' },
        { key: 'justification', label: 'Justification', type: 'textarea' },
        { key: 'priority', label: 'Priority', type: 'select', options: ['low', 'normal', 'high', 'critical'] },
      ],
    },
  };

  const openEdit = (kind, item) => {
    setEditKind(kind);
    setEditItem(item);
    const meta = editKindMeta[kind];
    const form = {};
    meta.fields.forEach((f) => { form[f.key] = item[f.key] ?? ''; });
    setEditForm(form);
    setRejectionReason('');
    setShowRejectInput(false);
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editItem) return;
    const meta = editKindMeta[editKind];
    // Build payload only with fields that changed and have a value
    const payload = {};
    meta.fields.forEach((f) => {
      if (editForm[f.key] !== undefined && editForm[f.key] !== '') {
        payload[f.key] = f.type === 'number' ? Number(editForm[f.key]) : editForm[f.key];
      }
    });
    if (Object.keys(payload).length === 0) {
      toast.error('No changes to save');
      return;
    }
    try {
      const url = `${API}/api/projects/${editItem.project_id}/${meta.endpoint}/${editItem.id}`;
      await axios[meta.method](url, payload, { headers });
      toast.success(`${meta.label} updated`);
      setShowEditDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to update ${meta.label}`);
    }
  };

  const handleApprovalAction = async (action) => {
    if (!editItem) return;
    const meta = editKindMeta[editKind];
    const url = `${API}/api/projects/${editItem.project_id}/${meta.endpoint}/${editItem.id}/${
      action === 'submit' ? 'submit-approval' : action
    }`;
    try {
      const body = action === 'reject' ? { reason: rejectionReason } : {};
      await axios.post(url, body, { headers });
      const label = { submit: 'Submitted for approval', approve: 'Approved', reject: 'Rejected' }[action];
      toast.success(`${meta.label}: ${label}`);
      setShowEditDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Action failed`);
    }
  };

  const approvalBadge = (item) => {
    const st = item?.approval_status;
    if (!st) return null;
    const map = {
      pending_approval: { className: 'bg-amber-100 text-amber-800', label: 'Pending approval' },
      approved:         { className: 'bg-green-100 text-green-800', label: 'Approved' },
      rejected:         { className: 'bg-red-100 text-red-800',     label: 'Rejected' },
    };
    const m = map[st];
    if (!m) return null;
    return <Badge className={`${m.className} text-xs`}>{m.label}</Badge>;
  };

  const approvalMeta = (item) => {
    if (!item) return null;
    if (item.approval_status === 'approved' && item.approved_by) {
      const when = item.approved_at ? new Date(item.approved_at).toLocaleDateString('en-GB') : '';
      return <span className="text-xs text-green-700">Approved by {item.approved_by}{when ? ` on ${when}` : ''}</span>;
    }
    if (item.approval_status === 'rejected' && item.rejected_by) {
      const when = item.rejected_at ? new Date(item.rejected_at).toLocaleDateString('en-GB') : '';
      return <span className="text-xs text-red-700">Rejected by {item.rejected_by}{when ? ` on ${when}` : ''}{item.rejection_reason ? ` — ${item.rejection_reason}` : ''}</span>;
    }
    if (item.approval_status === 'pending_approval' && item.approval_submitted_by) {
      return <span className="text-xs text-amber-700">Submitted by {item.approval_submitted_by}</span>;
    }
    return null;
  };

  // Keep old aliases used by the Digital Checklists tab JSX
  const handleCreateDigitalChecklist = () => openCreate('digital');
  const handleDeleteDigitalChecklist = (pid, id) => handleDelete('digital', pid, id);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/qa-overview`, { headers });
      setData(res.data);
    } catch (error) {
      console.error('Failed to fetch QA overview:', error);
      toast.error('Failed to load QA overview data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const stats = data?.stats || {};

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Dashboard
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardCheck className="h-7 w-7 text-blue-700" />
            QA & Change Requests Overview
          </h1>
          <p className="text-gray-700 mt-1">Cross-project quality assurance and approval management</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card data-testid="stat-qa-checklists">
          <CardContent className="p-4 text-center">
            <ClipboardCheck className="h-5 w-5 mx-auto mb-1 text-blue-700" />
            <span className="block text-2xl font-bold text-blue-700">{stats.qa_checklists || 0}</span>
            <p className="text-xs text-gray-700">QA Checklists</p>
            {stats.qa_checklists_pending > 0 && (
              <Badge className="mt-1 bg-amber-100 text-amber-700 text-xs">{stats.qa_checklists_pending} pending</Badge>
            )}
          </CardContent>
        </Card>
        <Card data-testid="stat-digital-checklists">
          <CardContent className="p-4 text-center">
            <FileCheck className="h-5 w-5 mx-auto mb-1 text-indigo-700" />
            <span className="block text-2xl font-bold text-indigo-700">{stats.digital_checklists || 0}</span>
            <p className="text-xs text-gray-700">Digital Checklists</p>
            {stats.digital_checklists_active > 0 && (
              <Badge className="mt-1 bg-blue-100 text-blue-700 text-xs">{stats.digital_checklists_active} active</Badge>
            )}
          </CardContent>
        </Card>
        <Card data-testid="stat-inspections">
          <CardContent className="p-4 text-center">
            <SearchIcon className="h-5 w-5 mx-auto mb-1 text-teal-700" />
            <span className="block text-2xl font-bold text-teal-700">{stats.inspections || 0}</span>
            <p className="text-xs text-gray-700">Inspections</p>
            {stats.inspections_failed > 0 && (
              <Badge className="mt-1 bg-red-100 text-red-700 text-xs">{stats.inspections_failed} failed</Badge>
            )}
          </CardContent>
        </Card>
        <Card data-testid="stat-change-requests">
          <CardContent className="p-4 text-center">
            <Layers className="h-5 w-5 mx-auto mb-1 text-purple-700" />
            <span className="block text-2xl font-bold text-purple-700">{stats.change_requests || 0}</span>
            <p className="text-xs text-gray-700">Change Requests</p>
            {stats.change_requests_pending > 0 && (
              <Badge className="mt-1 bg-amber-100 text-amber-700 text-xs">{stats.change_requests_pending} pending</Badge>
            )}
          </CardContent>
        </Card>
        <Card data-testid="stat-pending-approvals">
          <CardContent className="p-4 text-center">
            <Clock className="h-5 w-5 mx-auto mb-1 text-amber-700" />
            <span className="block text-2xl font-bold text-amber-700">{stats.pending_approvals || 0}</span>
            <p className="text-xs text-gray-700">Pending Approvals</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="qa-checklists" data-testid="tab-qa-checklists">QA Checklists</TabsTrigger>
          <TabsTrigger value="digital-checklists" data-testid="tab-digital-checklists">Digital Checklists</TabsTrigger>
          <TabsTrigger value="inspections" data-testid="tab-inspections">Inspections</TabsTrigger>
          <TabsTrigger value="change-requests" data-testid="tab-change-requests">Change Requests</TabsTrigger>
          <TabsTrigger value="pending-approvals" data-testid="tab-pending-approvals">Approvals</TabsTrigger>
        </TabsList>

        {/* QA Checklists Tab */}
        <TabsContent value="qa-checklists" className="space-y-3 mt-4">
          <h2 id="qa-checklists-section-heading" className="sr-only">QA checklists</h2>
          {canManage && (
            <div className="flex justify-end">
              <Button size="sm" onClick={() => openCreate('qa')} className="bg-blue-600 hover:bg-blue-700" data-testid="new-qa-checklist-btn">
                <Plus className="h-4 w-4 mr-1" /> New QA Checklist
              </Button>
            </div>
          )}
          {(data?.qa_checklists || []).length === 0 ? (
            <EmptyState icon={ClipboardCheck} text="No QA checklists found across projects" />
          ) : (
            (data.qa_checklists).map((item) => (
              <Card key={item.id} data-testid={`qa-${item.id}`} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={statusBadge(item.overall_status)} variant="secondary">{item.overall_status}</Badge>
                        {approvalBadge(item)}
                        <span className="text-xs text-gray-700">{item.project_title}</span>
                      </div>
                      <h3 className="font-semibold text-gray-900">{item.title}</h3>
                      <p className="text-sm text-gray-700">{item.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-700">
                        <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-green-500" /> {item.passed_count} passed</span>
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3 text-amber-700" /> {item.pending_count} pending</span>
                        <span>{item.total_items} total items</span>
                      </div>
                      {approvalMeta(item) && <div className="mt-1">{approvalMeta(item)}</div>}
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      <div className="w-20 text-center">
                        <Progress value={item.completion} className="h-2 mb-1" />
                        <span className="text-xs text-gray-700">{item.completion}%</span>
                      </div>
                      <Link to={`/projects/${item.project_id}`} aria-label="Open in project" data-testid={`open-project-${item.id}`}>
                        <Button variant="ghost" size="sm" aria-label="Open in project"><Eye className="h-4 w-4" aria-hidden="true" /></Button>
                      </Link>
                      {canManage && (
                        <Button variant="ghost" size="sm" className="text-blue-700 hover:bg-blue-50"
                          onClick={() => openEdit('qa', item)} aria-label="Edit QA checklist"
                          data-testid={`edit-qa-${item.id}`}>
                          <Pencil className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button variant="ghost" size="sm" className="text-red-700 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete('qa', item.project_id, item.id)} aria-label="Delete QA checklist"
                          data-testid={`delete-qa-${item.id}`}>
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Digital Checklists Tab */}
        <TabsContent value="digital-checklists" className="space-y-3 mt-4">
          <h2 id="digital-checklists-section-heading" className="sr-only">Digital checklists</h2>
          {canManage && (
            <div className="flex justify-between items-center">
              <p className="text-xs text-gray-700">
                Digital checklists shown here are the SAME records as in each project&apos;s &ldquo;Checklist&rdquo; tab — create, edit and delete are synchronised.
              </p>
              <Button
                size="sm"
                onClick={() => setShowCreateDialog(true)}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="new-digital-checklist-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> New Digital Checklist
              </Button>
            </div>
          )}
          {(data?.digital_checklists || []).length === 0 ? (
            <EmptyState icon={FileCheck} text="No digital checklists found across projects" />
          ) : (
            (data.digital_checklists).map((item) => (
              <Card key={item.id} data-testid={`dc-${item.id}`} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={statusBadge(item.status)} variant="secondary">{item.status}</Badge>
                        <Badge variant="outline" className="text-xs">{item.template_type}</Badge>
                        <span className="text-xs text-gray-700">{item.project_title}</span>
                      </div>
                      <h3 className="font-semibold text-gray-900">{item.title}</h3>
                      <p className="text-sm text-gray-700">{item.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-700">
                        <span>{item.completed_count}/{item.total_items} items completed</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      <div className="w-20 text-center">
                        <Progress value={item.completion} className="h-2 mb-1" />
                        <span className="text-xs text-gray-700">{item.completion}%</span>
                      </div>
                      <Link to={`/projects/${item.project_id}`} aria-label="Open in project" data-testid={`open-project-${item.id}`}>
                        <Button variant="ghost" size="sm" aria-label="Open in project"><Eye className="h-4 w-4" aria-hidden="true" /></Button>
                      </Link>
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-700 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteDigitalChecklist(item.project_id, item.id)}
                          title="Delete checklist"
                          data-testid={`delete-dc-${item.id}`}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Inspections Tab */}
        <TabsContent value="inspections" className="space-y-3 mt-4">
          <h2 id="inspections-section-heading" className="sr-only">Inspections</h2>
          {canManage && (
            <div className="flex justify-end">
              <Button size="sm" onClick={() => openCreate('inspection')} className="bg-blue-600 hover:bg-blue-700" data-testid="new-inspection-btn">
                <Plus className="h-4 w-4 mr-1" /> New Inspection
              </Button>
            </div>
          )}
          {(data?.inspections || []).length === 0 ? (
            <EmptyState icon={SearchIcon} text="No QA inspections found across projects" />
          ) : (
            (data.inspections).map((item) => (
              <Card key={item.id} data-testid={`insp-${item.id}`} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={statusBadge(item.status)} variant="secondary">{item.status}</Badge>
                        <Badge variant="outline" className="text-xs">{item.item_type}</Badge>
                        {approvalBadge(item)}
                        <span className="text-xs text-gray-700">{item.project_title}</span>
                      </div>
                      <h3 className="font-semibold text-gray-900">{item.item_name}</h3>
                      <p className="text-sm text-gray-700">{item.notes}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-700">
                        <span>Score: <strong>{item.score}</strong>/100</span>
                        <span>Inspector: {item.inspector}</span>
                        <span>Date: {item.inspection_date ? new Date(item.inspection_date).toLocaleDateString('en-GB') : 'N/A'}</span>
                      </div>
                      {approvalMeta(item) && <div className="mt-1">{approvalMeta(item)}</div>}
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold ${
                        item.score >= 80 ? 'bg-green-100 text-green-700' : item.score >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {item.score}
                      </div>
                      <Link to={`/projects/${item.project_id}`} aria-label="Open in project" data-testid={`open-project-${item.id}`}>
                        <Button variant="ghost" size="sm" aria-label="Open in project"><Eye className="h-4 w-4" aria-hidden="true" /></Button>
                      </Link>
                      {canManage && (
                        <Button variant="ghost" size="sm" className="text-blue-700 hover:bg-blue-50"
                          onClick={() => openEdit('inspection', item)} aria-label="Edit inspection"
                          data-testid={`edit-insp-${item.id}`}>
                          <Pencil className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button variant="ghost" size="sm" className="text-red-700 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete('inspection', item.project_id, item.id)} aria-label="Delete inspection"
                          data-testid={`delete-insp-${item.id}`}>
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Change Requests Tab */}
        <TabsContent value="change-requests" className="space-y-3 mt-4">
          <h2 id="change-requests-section-heading" className="sr-only">Change requests</h2>
          {canManage && (
            <div className="flex justify-end">
              <Button size="sm" onClick={() => openCreate('change')} className="bg-blue-600 hover:bg-blue-700" data-testid="new-change-request-btn">
                <Plus className="h-4 w-4 mr-1" /> New Change Request
              </Button>
            </div>
          )}
          {(data?.change_requests || []).length === 0 ? (
            <EmptyState icon={Layers} text="No change requests found across projects" />
          ) : (
            (data.change_requests).map((item) => (
              <Card key={item.id} data-testid={`cr-${item.id}`} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={statusBadge(item.status)} variant="secondary">{item.status}</Badge>
                        <Badge variant="outline" className="text-xs">{item.priority || 'normal'}</Badge>
                        {approvalBadge(item)}
                        <span className="text-xs text-gray-700">{item.project_title}</span>
                      </div>
                      <h3 className="font-semibold text-gray-900">{item.title}</h3>
                      <p className="text-sm text-gray-700">{item.description}</p>
                      {item.justification && <p className="text-xs text-gray-700 mt-1">Justification: {item.justification}</p>}
                      {approvalMeta(item) && <div className="mt-1">{approvalMeta(item)}</div>}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Link to={`/projects/${item.project_id}`} aria-label="Open in project" data-testid={`open-project-${item.id}`}>
                        <Button variant="ghost" size="sm" aria-label="Open in project"><Eye className="h-4 w-4" aria-hidden="true" /></Button>
                      </Link>
                      {canManage && (
                        <Button variant="ghost" size="sm" className="text-blue-700 hover:bg-blue-50"
                          onClick={() => openEdit('change', item)} aria-label="Edit change request"
                          data-testid={`edit-cr-${item.id}`}>
                          <Pencil className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button variant="ghost" size="sm" className="text-red-700 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete('change', item.project_id, item.id)} aria-label="Delete change request"
                          data-testid={`delete-cr-${item.id}`}>
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Pending Approvals Tab */}
        <TabsContent value="pending-approvals" className="space-y-3 mt-4">
          <h2 id="pending-approvals-section-heading" className="sr-only">Pending approvals</h2>
          {(data?.pending_approvals || []).length === 0 ? (
            <EmptyState icon={Clock} text="No pending approvals across projects" />
          ) : (
            (data.pending_approvals).map((item, idx) => (
              <Card key={item.id || idx} data-testid={`approval-${item.id || idx}`} className="hover:shadow-md transition-shadow border-l-4 border-l-amber-400">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className="bg-amber-100 text-amber-800" variant="secondary">Pending Approval</Badge>
                        <Badge variant="outline" className="text-xs">{item.entity_type}</Badge>
                        <span className="text-xs text-gray-700">{item.project_title}</span>
                      </div>
                      <h3 className="font-semibold text-gray-900">{item.title || item.name || item.description || 'Unnamed'}</h3>
                      {item.pending_changes && (
                        <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2">
                          <p className="text-xs font-medium text-amber-800 mb-1">Proposed Changes:</p>
                          {Object.entries(item.pending_changes).map(([field, value]) => (
                            <p key={field} className="text-xs text-gray-700"><strong>{field}:</strong> {String(value)}</p>
                          ))}
                        </div>
                      )}
                    </div>
                    <Link to={`/projects/${item.project_id}`}>
                      <Button variant="ghost" size="sm"><Eye className="h-4 w-4 mr-1" /> View Project</Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Unified Create dialog (Digital / QA / Inspection / Change Request) */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md" data-testid="new-dc-dialog">
          <DialogHeader>
            <DialogTitle>{createKindMeta[createKind].titleKey}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {createKind === 'digital' && (
              <p className="text-xs text-gray-700">
                The checklist will appear here AND in the selected project&apos;s &ldquo;Checklist&rdquo; tab — both views share the same record.
              </p>
            )}
            <div>
              <Label>Project *</Label>
              <Select value={newCl.project_id || ''} onValueChange={(v) => setNewCl({ ...newCl, project_id: v })}>
                <SelectTrigger data-testid="new-dc-project-select"><SelectValue placeholder="Select a project…" /></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (<SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title *</Label>
              <Input value={newCl.title} onChange={(e) => setNewCl({ ...newCl, title: e.target.value })} placeholder="Short, descriptive title" data-testid="new-dc-title-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={newCl.description} onChange={(e) => setNewCl({ ...newCl, description: e.target.value })} placeholder="Short context for reviewers" />
            </div>
            {createKind === 'qa' && (
              <div>
                <Label>Assigned to (username, optional)</Label>
                <Input value={newCl.assigned_to} onChange={(e) => setNewCl({ ...newCl, assigned_to: e.target.value })} placeholder="e.g., pm_demo" />
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-new-dc-btn">
                Create {createKindMeta[createKind].label}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unified Edit dialog (QA Checklist / Inspection / Change Request) */}
      <Dialog open={showEditDialog} onOpenChange={(o) => { if (!o) setShowEditDialog(false); }}>
        <DialogContent className="max-w-lg" data-testid="edit-qa-dialog">
          <DialogHeader>
            <DialogTitle>Edit {editKindMeta[editKind].label}</DialogTitle>
          </DialogHeader>
          {editItem && (
            <div className="space-y-4 pt-2">
              {/* Approval state banner */}
              <div className="flex items-center gap-2 flex-wrap" data-testid="edit-approval-state">
                <span className="text-xs text-gray-700">Approval status:</span>
                {editItem.approval_status ? approvalBadge(editItem) : <Badge className="bg-gray-100 text-gray-700 text-xs">Not submitted</Badge>}
                {approvalMeta(editItem)}
              </div>

              {/* Editable fields */}
              {editKindMeta[editKind].fields.map((f) => (
                <div key={f.key}>
                  <Label>{f.label}</Label>
                  {f.type === 'textarea' ? (
                    <Textarea
                      value={editForm[f.key] ?? ''}
                      onChange={(e) => setEditForm({ ...editForm, [f.key]: e.target.value })}
                      rows={3}
                      data-testid={`edit-field-${f.key}`}
                    />
                  ) : f.type === 'select' ? (
                    <Select
                      value={editForm[f.key] ?? ''}
                      onValueChange={(v) => setEditForm({ ...editForm, [f.key]: v })}
                    >
                      <SelectTrigger data-testid={`edit-field-${f.key}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                      <SelectContent>
                        {f.options.map((opt) => (<SelectItem key={opt} value={opt}>{opt}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      type={f.type === 'number' ? 'number' : 'text'}
                      value={editForm[f.key] ?? ''}
                      onChange={(e) => setEditForm({ ...editForm, [f.key]: e.target.value })}
                      data-testid={`edit-field-${f.key}`}
                    />
                  )}
                </div>
              ))}

              {/* Approval actions (Admin / PM only) */}
              {canApprove && (
                <div className="border-t pt-3 space-y-2" data-testid="approval-actions">
                  <p className="text-xs font-medium text-gray-700">Approval workflow</p>
                  {!showRejectInput ? (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => handleApprovalAction('submit')}
                        disabled={editItem.approval_status === 'pending_approval'}
                        data-testid="approval-submit-btn"
                      >
                        <Send className="h-3.5 w-3.5 mr-1" />
                        {editItem.approval_status === 'pending_approval' ? 'Already submitted' : 'Submit for Approval'}
                      </Button>
                      <Button
                        type="button" size="sm" className="bg-green-700 hover:bg-green-800"
                        onClick={() => handleApprovalAction('approve')}
                        disabled={editItem.approval_status === 'approved'}
                        data-testid="approval-approve-btn"
                      >
                        <ThumbsUp className="h-3.5 w-3.5 mr-1" /> Approve
                      </Button>
                      <Button
                        type="button" size="sm" variant="destructive"
                        onClick={() => setShowRejectInput(true)}
                        disabled={editItem.approval_status === 'rejected'}
                        data-testid="approval-reject-btn"
                      >
                        <ThumbsDown className="h-3.5 w-3.5 mr-1" /> Reject
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2 bg-red-50 border border-red-200 rounded p-2">
                      <Label className="text-xs">Rejection reason (optional)</Label>
                      <Textarea
                        rows={2}
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="Briefly explain why this is being rejected"
                        data-testid="approval-reject-reason"
                      />
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => { setShowRejectInput(false); setRejectionReason(''); }}>Cancel</Button>
                        <Button size="sm" variant="destructive" onClick={() => handleApprovalAction('reject')} data-testid="approval-confirm-reject-btn">
                          Confirm Reject
                        </Button>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-gray-700">Approval actions are restricted to Administrators and Project Managers.</p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2 border-t">
                <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
                <Button onClick={handleSaveEdit} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-edit-btn">
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const EmptyState = ({ icon: Icon, text }) => (
  <Card>
    <CardContent className="flex flex-col items-center justify-center py-12">
      <Icon className="h-10 w-10 text-gray-300 mb-3" />
      <p className="text-gray-700">{text}</p>
    </CardContent>
  </Card>
);

export default QAOverview;
